import { z } from 'zod';
/**
 * Contratti di autenticazione condivisi tra API e front-end.
 * I due ruoli sono quelli del contratto: admin (Fametrue) e client (azienda cliente).
 */
export const ROLES = ['admin', 'client'];
export const roleSchema = z.enum(ROLES);
export const USER_STATUSES = ['invited', 'active', 'disabled'];
/**
 * Policy password allineata a NIST SP 800-63B: lunghezza minima alta, nessuna
 * regola di composizione (che spinge gli utenti verso pattern prevedibili),
 * tetto massimo per non esporre l'hashing Argon2 a input arbitrariamente lunghi.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, { message: 'password_too_short' })
    .max(PASSWORD_MAX_LENGTH, { message: 'password_too_long' });
export const emailSchema = z.email({ message: 'email_invalid' }).max(254).toLowerCase().trim();
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
/** Token opaco dell'invito: la stessa forma per l'accettazione e per la lettura. */
export const inviteTokenSchema = z.string().min(20).max(200);
export const acceptInviteRequestSchema = z.object({
    token: inviteTokenSchema,
    password: passwordSchema,
});
export const inviteLookupQuerySchema = z.object({ token: inviteTokenSchema });
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
    newPassword: passwordSchema,
});
/**
 * Brand visibili all'utente: un sottoinsieme dei brand della sua company,
 * scelto dall'admin. Solo per role=client. Vuoto è uno stato legittimo e
 * significa che l'utente non vede alcun brand.
 */
const brandIdsSchema = z.array(z.uuid()).max(500);
export const createUserRequestSchema = z.object({
    email: emailSchema,
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    role: roleSchema,
    /** Obbligatorio per role=client, deve essere assente per role=admin. */
    companyId: z.uuid().optional(),
    /** Deve essere vuoto per role=admin. */
    brandIds: brandIdsSchema.default([]),
});
/** Sostituisce per intero l'elenco dei brand visibili all'utente. */
export const updateUserBrandsRequestSchema = z.object({
    brandIds: brandIdsSchema,
});
export const createCompanyRequestSchema = z.object({
    name: z.string().trim().min(1).max(200),
});
export const DEFAULT_COMPANY_FEATURES = {
    insight: true,
    decision: true,
    intelligence: true,
};
/** Codici di errore stabili: la UI li mappa sulle stringhe ITA/ENG localizzate. */
export const AUTH_ERROR_CODES = [
    'invalid_credentials',
    'current_password_invalid',
    'account_disabled',
    'account_locked',
    'user_never_activated',
    'cannot_disable_self',
    'cannot_reinvite_self',
    'cannot_delete_self',
    'invite_invalid',
    'invite_expired',
    'session_expired',
    'csrf_invalid',
    'rate_limited',
    'password_too_short',
    'password_too_long',
    'password_too_common',
    'email_already_exists',
    'brand_not_in_company',
    'forbidden',
    'level_not_enabled',
    'validation_failed',
    'not_found',
    'internal_error',
];
//# sourceMappingURL=auth.js.map